"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useCustomer = useCustomer;
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = require("react");
var _nanoid = require("nanoid");
var _contexts = require("../contexts");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const AuthTypes = Object.freeze({
  GUEST: 'guest',
  REGISTERED: 'registered'
});

// This value represents the max age in milliseconds a customer can be before they are
// no longer considered a "new" customer.
// E.g. If a customers creation date is older than 2 seconds it will no longer be considered
// a new customer.
const NEW_CUSTOMER_MAX_AGE = 2 * 1000; // 2 seconds in milliseconds

function useCustomer() {
  const api = (0, _contexts.useCommerceAPI)();
  const {
    customer,
    setCustomer
  } = (0, _react.useContext)(_contexts.CustomerContext);
  const self = (0, _react.useMemo)(() => {
    return _objectSpread(_objectSpread({}, customer), {}, {
      /**
       * Returns boolean value whether the user data is initialized
       */
      get isInitialized() {
        return !!(customer !== null && customer !== void 0 && customer.customerId);
      },
      /**
       * Returns boolean value whether the customer is of type `registered` or not.
       */
      get isRegistered() {
        return (customer === null || customer === void 0 ? void 0 : customer.authType) === AuthTypes.REGISTERED;
      },
      /**
       * Returns boolean value whether the customer is of type `guest` or not.
       */
      get isGuest() {
        return (customer === null || customer === void 0 ? void 0 : customer.authType) === AuthTypes.GUEST;
      },
      /**
       * Returns if this customer is newly registered.
       */
      get isNew() {
        if (!customer || customer.authType !== 'registered') return false;
        const lastLoginTimeStamp = Date.parse(customer.lastLoginTime);
        const creationTimeStamp = Date.parse(customer.creationDate);
        return lastLoginTimeStamp - creationTimeStamp < NEW_CUSTOMER_MAX_AGE;
      },
      /** Returns the customer's saved addresses with the 'preferred' address in the first index */
      get addresses() {
        // TODO: This performs array manipulation every time it is accessed; should it be
        // changed to only execute once and save the result?
        if (!(customer !== null && customer !== void 0 && customer.addresses)) {
          return undefined;
        }
        // Cloned so that we can manipulate the order
        const addresses = [...customer.addresses];
        const preferredIndex = addresses.findIndex(addr => addr.preferred);
        if (preferredIndex === -1) return addresses;
        const [preferred] = addresses.splice(preferredIndex, 1);
        return [preferred, ...addresses];
      },
      /**
       * Log in customer account.
       *
       * @param {object} credentials
       * @param {string} credentials.email
       * @param {string} credentials.password
       */
      login(credentials) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const skeletonCustomer = yield api.auth.login(credentials);
          if (skeletonCustomer.authType === 'guest') {
            setCustomer(skeletonCustomer);
          } else {
            // @ts-ignore
            const customer = yield api.shopperCustomers.getCustomer({
              parameters: {
                customerId: skeletonCustomer.customerId
              }
            });
            setCustomer(customer);
          }
        })();
      },
      /**
       * Log out current customer.
       * and retrive a guest access token
       */
      logout() {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const customer = yield api.auth.logout();
          setCustomer(customer);
        })();
      },
      /**
       * Fetch current customer information.
       */
      getCustomer() {
        return (0, _asyncToGenerator2.default)(function* () {
          setCustomer(// @ts-ignore
          yield api.shopperCustomers.getCustomer({
            parameters: {
              customerId: customer.customerId
            }
          }));
        })();
      },
      /**
       * Register a new customer account.
       *
       * @param {object} data
       * @param {string} data.email
       * @param {string} data.firstName
       * @param {string} data.lastName
       * @param {string} data.password
       * @param {string} data.birthday
       * @param {string} data.phoneMobile
       * @param {string} data.salutation
       * @param {boolean} data.newsletter
       */
      registerCustomer(data) {
        return (0, _asyncToGenerator2.default)(function* () {
          // TODO: investigate how to submit/include the checkbox input
          // for receiving email communication.
          let customerData = _objectSpread(_objectSpread({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            login: data.email,
            phoneHome: data.phoneMobile || '',
            salutation: data.salutation || ''
          }, data.newsletter !== undefined && {
            c_newsletter: !!data.newsletter
          }), data.newsletterSource && {
            c_newsletterSource: data.newsletterSource
          });
          if (data.birthday) {
            customerData = _objectSpread(_objectSpread({}, customerData), {}, {
              // @ts-ignore
              birthday: data.birthday
            });
          }
          const body = {
            customer: customerData,
            password: data.password
          };

          // @ts-ignore
          const response = yield api.shopperCustomers.registerCustomer({
            body
          });
          // Check for error json response
          if (response.title && response.type && (response.statusCode || response.detail)) {
            throw new Error(response.statusCode || response.detail);
          }

          // Send a new login request with the given credentials to ensure tokens are updated.
          yield self.login({
            email: data.email,
            password: data.password
          });
        })();
      },
      /**
       * Update customer information.
       *
       * @param {object} data
       * @param {string} data.email
       * @param {string} data.firstName
       * @param {string} data.lastName
       * @param {string} data.phone
       * @param {string} data.c_newsletter
       * @param {string} data.c_aboAlerts
       * @param {string} data.c_decalcifyAlerts
       */
      updateCustomer(data) {
        return (0, _asyncToGenerator2.default)(function* () {
          const body = {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneHome: data.phone,
            c_newsletter: data.c_newsletter,
            c_aboAlerts: data.c_aboAlerts,
            c_decalcifyAlerts: data.c_decalcifyAlerts,
            // NOTE/ISSUE
            // The sdk is allowing you to change your email to an already-existing email.
            // I would expect an error. We also want to keep the email and login the same
            // for the customer, but the sdk isn't changing the login when we submit an
            // updated email. This will lead to issues where you change your email but end
            // up not being able to login since 'login' will no longer match the email.
            email: data.email,
            login: data.email
          };

          // @ts-ignore
          const response = yield api.shopperCustomers.updateCustomer({
            body,
            parameters: {
              customerId: customer.customerId
            }
          });

          // Check for error json response
          if (response.detail && response.title && response.type) {
            throw new Error(response.detail);
          }

          // This previous request does return the updated customer profile, however it does
          // not include the 'entire' customer. It is missing address and payment methods.
          // We need to refetch the customer to make sure everything is up to date.
          yield self.getCustomer();
        })();
      },
      /**
       * Update customer password.
       *
       * @param {object} data
       * @param {string} data.currentPassword - The old password.
       * @param {string} data.password - The new password.
       */
      updatePassword(data, email) {
        return (0, _asyncToGenerator2.default)(function* () {
          const body = {
            password: data.password,
            currentPassword: data.currentPassword
          };

          // Note that we're using the raw response here. This request does not return
          // data when successful, but the sdk tries to parse the json (potential sdk bug).
          // @ts-ignore
          const rawResponse = yield api.shopperCustomers.updateCustomerPassword({
            body,
            parameters: {
              customerId: customer.customerId
            }
          }, true);

          // Success has no json response to parse, but errors do, so handle that here.
          if (rawResponse.status >= 400) {
            const json = yield rawResponse.json();
            // Check for error json response
            if (json.detail && json.title && json.type) {
              throw new Error(json.detail);
            }
          }

          // Fetch a new SLAS JWT to update the invalid one in client app state
          const credentials = {
            email: email,
            password: data.password
          };
          yield self.login(credentials);
        })();
      },
      /**
       * @todo - Backend set up required
       * @param {string} login - customer email address
       */
      getResetPasswordToken(login) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperCustomers.getResetPasswordToken({
            body: {
              login
            }
          });

          // Check for error json response
          if (response.detail && response.title && response.type) {
            throw new Error(response.detail);
          }
        })();
      },
      /**
       * Send a password reset email to the customer.
       * @param {string} email the emails og the user
       * @returns Promise
       */
      sendPasswordResetEmail(email) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          return yield api.shopperPassword.sendPasswordResetEmail({
            body: {
              identification: email,
              type: 'email'
            }
          });
        })();
      },
      /**
       * Add a new saved address.
       *
       * @external Address
       * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
       */
      addSavedAddress(address) {
        return (0, _asyncToGenerator2.default)(function* () {
          // The `addressId` field is required to save the customer's address to their account.
          // Rather than make the customer provide a name/id, we can generate a unique id behind
          // the scenes instead. This is only useful if you are not displaying the addr name/id
          // in the UI, which we aren't.
          const body = _objectSpread({
            addressId: (0, _nanoid.nanoid)()
          }, address);

          // @ts-ignore
          yield api.shopperCustomers.createCustomerAddress({
            body,
            parameters: {
              customerId: customer.customerId
            }
          });

          // This endpoint does not return the updated customer object, so we manually fetch it
          yield self.getCustomer();
        })();
      },
      /**
       * Update a saved address.
       *
       * @external Address
       * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
       */
      updateSavedAddress(address) {
        return (0, _asyncToGenerator2.default)(function* () {
          const body = address;

          // @ts-ignore
          yield api.shopperCustomers.updateCustomerAddress({
            body,
            parameters: {
              customerId: customer.customerId,
              addressName: address.addressId
            }
          });

          // This endpoint does not return the updated customer object, so we manually fetch it
          yield self.getCustomer();
        })();
      },
      /**
       * Add a new saved payment instrument.
       *
       * @external PaymentInstrument
       * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customerpaymentinstrumentrequest
       */
      addSavedPaymentInstrument(paymentInstrument) {
        return (0, _asyncToGenerator2.default)(function* () {
          const body = _objectSpread(_objectSpread({
            bankRoutingNumber: '',
            giftCertificateCode: ''
          }, paymentInstrument), {}, {
            paymentCard: _objectSpread(_objectSpread({}, paymentInstrument.paymentCard), {}, {
              securityCode: undefined
            })
          });
          // @ts-ignore
          yield api.shopperCustomers.createCustomerPaymentInstrument({
            body,
            parameters: {
              customerId: customer.customerId
            }
          });

          // This endpoint does not return the updated customer object, so we manually fetch it
          yield self.getCustomer();
        })();
      },
      /**
       * Remove a saved payment instrument.
       *
       * @param {string} paymentInstrumentId - The id of the payment payment instrument.
       */
      removeSavedPaymentInstrument(paymentInstrumentId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // This SDK method must be called with `true` as second argument to avoid an error in
          // the sdk where it tries parsing json from an empty http response.
          // @ts-ignore
          yield api.shopperCustomers.deleteCustomerPaymentInstrument({
            parameters: {
              customerId: customer.customerId,
              paymentInstrumentId
            }
          }, true);

          // This endpoint does not return the updated customer object, so we manually fetch it
          yield self.getCustomer();
        })();
      },
      /**
       * Remove a saved address.
       *
       * @param {string} addressId - The id of the saved address.
       */
      removeSavedAddress(addressId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // This SDK method must be called with `true` as second argument to avoid an error in
          // the sdk where it tries parsing json from an empty http response.
          // @ts-ignore
          yield api.shopperCustomers.removeCustomerAddress({
            parameters: {
              customerId: customer.customerId,
              addressName: addressId
            }
          }, true);

          // This endpoint does not return the updated customer object, so we manually fetch it
          yield self.getCustomer();
        })();
      },
      getCustomerOrders(params) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperCustomers.getCustomerOrders({
            parameters: _objectSpread({
              customerId: customer.customerId,
              offset: 0,
              limit: 10
            }, params)
          });
          return response;
        })();
      },
      getOrder(orderNo) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperOrders.getOrder({
            parameters: {
              orderNo
            }
          });
          return response;
        })();
      },
      getCustomerOrderProductsDetail(ids) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperProducts.getProducts({
            parameters: {
              ids: ids.join(',')
            }
          });
          const productMap = response.data.reduce((result, item) => {
            const key = item.id;
            result[key] = item;
            return result;
          }, {});
          return productMap;
        })();
      }
    });
  }, [customer, setCustomer]);
  return self;
}